/* ============================================================
   submit-item.js  -  Gold Standard Buys  (v2.1)
   Drives sections/submit-your-items.liquid
   ------------------------------------------------------------
   No dependencies. Loaded with `defer`.

   Two paths through one form:
     quick : step 0 -> step 1 -> submit
     full  : step 0 -> step 1 -> step 2 -> step 3 -> submit

   The form posts through Shopify's {% form 'contact' %}, which
   emails every contact[...] field. Fields without a name attribute
   (availability, the quick-path note, photo URLs) reach the email
   only because composeBody() writes them into contact[body] before
   the post. If that function ever throws, the visitor sees a toast
   and the submit is blocked - a silent partial send is worse than
   a visible failure.

   VALIDATION OWNERSHIP
   The section deliberately carries `novalidate` and no native
   `required` attributes. Native validation cannot be used here:
   fields live in step containers that are display:none on other
   steps, and the browser aborts submission on a hidden invalid
   control without showing the visitor anything. All validation is
   below, and it only ever inspects the step that is on screen.
   ============================================================ */

(function () {
  'use strict';

  var root = document.querySelector('.gsb-submit');
  if (!root) return;

  var form = document.getElementById('gsb-submit-form');
  if (!form) return;

  /* ---------- Config from the section ---------- */

  var CLOUD    = root.getAttribute('data-cloud-name') || '';
  var PRESET   = root.getAttribute('data-upload-preset') || '';
  var MAX_PHOTOS = parseInt(root.getAttribute('data-max-photos'), 10) || 8;
  var MAX_MB   = parseInt(root.getAttribute('data-max-file-mb'), 10) || 10;
  var FORMATS  = (root.getAttribute('data-allowed-formats') || 'jpg,jpeg,png,webp,heic')
                   .split(',').map(function (s) { return s.trim().toLowerCase(); });

  /* ---------- Elements ---------- */

  var progress   = form.parentNode.querySelector('[data-gsb-progress]') || root.querySelector('[data-gsb-progress]');
  var stepDots   = root.querySelectorAll('[data-step-dot]');
  var steps      = form.querySelectorAll('.gsb-stepview');
  var pathInput  = form.querySelector('[data-gsb-path]');
  var honeypot   = form.querySelector('[data-gsb-honeypot]');
  var bodyInput  = form.querySelector('[data-gsb-body]');
  var toastEl    = root.querySelector('[data-gsb-toast]');

  var chosenBox   = form.querySelector('[data-gsb-chosen]');
  var chosenLabel = form.querySelector('[data-gsb-chosen-label]');
  var changeBtn   = form.querySelector('[data-gsb-change]');

  var quickHead = form.querySelector('[data-gsb-quick-head]');
  var fullHead  = form.querySelector('[data-gsb-full-head]');
  var quickMsg  = form.querySelector('[data-gsb-quick-msg]');
  var quickFine = form.querySelector('[data-gsb-quick-fine]');

  var step1Next   = form.querySelector('[data-gsb-step1-next]');
  var quickSubmit = form.querySelector('[data-gsb-quick-submit]');

  var messageQuick = form.querySelector('[data-gsb-message]');
  var descField    = form.querySelector('[data-gsb-description]');
  var messageFull  = form.querySelector('[data-gsb-message-full]');

  var uploadBtn = form.querySelector('[data-gsb-upload]');
  var thumbsBox = form.querySelector('[data-gsb-thumbs]');
  var reviewBox = form.querySelector('[data-gsb-review]');

  /* ---------- State ---------- */

  var path = '';        // 'quick' | 'full'
  var current = 0;
  var photos = [];      // { id, url, name, status }
  var submitting = false;
  var fileInput = null;

  /* ---------- Small helpers ---------- */

  function val(el) { return el && el.value ? el.value.trim() : ''; }

  function toast(message, isError) {
    if (!toastEl) return;
    toastEl.textContent = message;
    toastEl.classList.toggle('is-error', !!isError);
    toastEl.classList.add('is-shown');
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(function () {
      toastEl.classList.remove('is-shown');
    }, 4200);
  }

  function scrollToForm() {
    var top = root.getBoundingClientRect().top + window.pageYOffset - 24;
    try {
      window.scrollTo({ top: top, behavior: 'smooth' });
    } catch (e) {
      window.scrollTo(0, top);
    }
  }

  /* ---------- Step machine ---------- */

  function stepsForPath() {
    return path === 'quick' ? [0, 1] : [0, 1, 2, 3];
  }

  function showStep(n) {
    current = n;

    for (var i = 0; i < steps.length; i++) {
      var el = steps[i];
      el.classList.toggle('is-active', parseInt(el.getAttribute('data-step'), 10) === n);
    }

    // The progress rail only means something on the three-step path.
    if (progress) {
      var show = path === 'full' && n >= 1;
      progress.hidden = !show;
      for (var d = 0; d < stepDots.length; d++) {
        var dot = stepDots[d];
        var num = parseInt(dot.getAttribute('data-step-dot'), 10);
        dot.classList.toggle('is-current', num === n);
        dot.classList.toggle('is-done', num < n);
      }
    }

    if (n > 0) scrollToForm();

    var firstField = steps[n] && steps[n].querySelector('.gsb-input:not([hidden])');
    if (n === 1 && firstField) {
      setTimeout(function () { firstField.focus({ preventScroll: true }); }, 180);
    }
  }

  function choosePath(next) {
    path = next;
    if (pathInput) pathInput.value = next;

    var isQuick = next === 'quick';

    if (quickHead) quickHead.hidden = !isQuick;
    if (fullHead)  fullHead.hidden  = isQuick;
    if (quickMsg)  quickMsg.hidden  = !isQuick;
    if (quickFine) quickFine.hidden = !isQuick;

    if (step1Next)   step1Next.hidden   = isQuick;
    if (quickSubmit) quickSubmit.hidden = !isQuick;

    if (chosenBox && chosenLabel) {
      chosenLabel.textContent = isQuick
        ? 'Booking a visit — contact details only'
        : 'Sharing details and photos first';
      chosenBox.hidden = false;
    }

    showStep(1);
  }

  function resetToChooser() {
    path = '';
    if (pathInput) pathInput.value = '';
    if (chosenBox) chosenBox.hidden = true;
    if (progress) progress.hidden = true;
    showStep(0);
  }

  /* ---------- Validation ---------- */

  function setError(input, message) {
    var field = input.closest('.gsb-field');
    var slot = field ? field.querySelector('[data-error]') : null;
    if (message) {
      input.classList.add('is-invalid');
      input.setAttribute('aria-invalid', 'true');
      if (slot) { slot.textContent = message; slot.classList.add('is-shown'); }
    } else {
      input.classList.remove('is-invalid');
      input.removeAttribute('aria-invalid');
      if (slot) { slot.textContent = ''; slot.classList.remove('is-shown'); }
    }
  }

  function checkField(input) {
    var name = (input.getAttribute('name') || '');
    var value = val(input);

    if (!value) {
      setError(input, 'This field is required.');
      return false;
    }

    if (name.indexOf('[email]') > -1 && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      setError(input, 'Enter a valid email address, for example name@example.com.');
      return false;
    }

    if (name.indexOf('[phone]') > -1 && value.replace(/\D/g, '').length < 7) {
      setError(input, 'Enter a phone number we can reach you on.');
      return false;
    }

    setError(input, '');
    return true;
  }

  function validateStep(n) {
    var container = steps[n];
    if (!container) return true;

    var fields = container.querySelectorAll('[data-required]');
    var ok = true;
    var firstBad = null;

    for (var i = 0; i < fields.length; i++) {
      var input = fields[i];
      // Skip anything the current path has hidden.
      if (input.offsetParent === null) continue;
      if (!checkField(input)) {
        ok = false;
        if (!firstBad) firstBad = input;
      }
    }

    if (!ok && firstBad) {
      firstBad.focus();
      toast('Please check the highlighted fields.', true);
    }
    return ok;
  }

  form.addEventListener('input', function (e) {
    var el = e.target;
    if (el.classList && el.classList.contains('is-invalid')) checkField(el);
  });

  /* ---------- Photos ---------- */

  function ensureFileInput() {
    if (fileInput) return fileInput;
    fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.multiple = true;
    fileInput.style.display = 'none';
    fileInput.addEventListener('change', function () {
      handleFiles(Array.prototype.slice.call(fileInput.files));
      fileInput.value = '';
    });
    root.appendChild(fileInput);
    return fileInput;
  }

  function extOk(file) {
    var ext = (file.name.split('.').pop() || '').toLowerCase();
    return FORMATS.indexOf(ext) > -1 || /^image\//.test(file.type);
  }

  function handleFiles(files) {
    if (!CLOUD || !PRESET) {
      toast('Photo upload is unavailable right now. You can still submit without photos.', true);
      return;
    }

    var room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      toast('You can add up to ' + MAX_PHOTOS + ' photos.', true);
      return;
    }

    files.slice(0, room).forEach(function (file) {
      if (!extOk(file)) {
        toast(file.name + ' is not a supported image.', true);
        return;
      }
      if (file.size > MAX_MB * 1024 * 1024) {
        toast(file.name + ' is larger than ' + MAX_MB + 'MB.', true);
        return;
      }
      uploadFile(file);
    });

    if (files.length > room) {
      toast('Added the first ' + room + '. Limit is ' + MAX_PHOTOS + ' photos.', true);
    }
  }

  function uploadFile(file) {
    var id = 'p' + Date.now() + Math.random().toString(36).slice(2, 7);
    var record = { id: id, url: '', name: file.name, status: 'uploading' };
    photos.push(record);

    var thumb = document.createElement('div');
    thumb.className = 'gsb-thumb is-loading';
    thumb.setAttribute('data-photo-id', id);
    thumb.innerHTML =
      '<img alt="">' +
      '<span class="gsb-thumb__bar"></span>' +
      '<button type="button" class="gsb-thumb__remove" aria-label="Remove photo">&times;</button>';
    thumbsBox.appendChild(thumb);

    var preview = thumb.querySelector('img');
    var bar = thumb.querySelector('.gsb-thumb__bar');
    var objectUrl = URL.createObjectURL(file);
    preview.src = objectUrl;

    thumb.querySelector('.gsb-thumb__remove').addEventListener('click', function () {
      removePhoto(id);
    });

    var data = new FormData();
    data.append('file', file);
    data.append('upload_preset', PRESET);

    var xhr = new XMLHttpRequest();
    xhr.open('POST', 'https://api.cloudinary.com/v1_1/' + CLOUD + '/image/upload');

    xhr.upload.addEventListener('progress', function (e) {
      if (e.lengthComputable && bar) {
        bar.style.width = Math.round((e.loaded / e.total) * 100) + '%';
      }
    });

    xhr.addEventListener('load', function () {
      URL.revokeObjectURL(objectUrl);
      var res = null;
      try { res = JSON.parse(xhr.responseText); } catch (err) { res = null; }

      if (xhr.status >= 200 && xhr.status < 300 && res && res.secure_url) {
        record.url = res.secure_url;
        record.status = 'done';
        thumb.classList.remove('is-loading');
        if (bar) bar.style.width = '100%';
      } else {
        record.status = 'error';
        thumb.classList.remove('is-loading');
        thumb.classList.add('is-error');
        toast('Could not upload ' + file.name + '. You can remove it and try again.', true);
      }
    });

    xhr.addEventListener('error', function () {
      URL.revokeObjectURL(objectUrl);
      record.status = 'error';
      thumb.classList.remove('is-loading');
      thumb.classList.add('is-error');
      toast('Upload failed. Check your connection and try again.', true);
    });

    xhr.send(data);
  }

  function removePhoto(id) {
    photos = photos.filter(function (p) { return p.id !== id; });
    var el = thumbsBox.querySelector('[data-photo-id="' + id + '"]');
    if (el) el.remove();
  }

  function uploadedPhotos() {
    return photos.filter(function (p) { return p.status === 'done' && p.url; });
  }

  function stillUploading() {
    return photos.some(function (p) { return p.status === 'uploading'; });
  }

  if (uploadBtn) {
    uploadBtn.addEventListener('click', function () {
      ensureFileInput().click();
    });
  }

  /* ---------- Review ---------- */

  function collect() {
    return {
      'Request type': path === 'quick' ? 'Book a visit' : 'Details and photos first',
      'Name':         val(form.querySelector('[name="contact[name]"]')),
      'Phone':        val(form.querySelector('[name="contact[phone]"]')),
      'Email':        val(form.querySelector('[name="contact[email]"]')),
      'ZIP':          val(form.querySelector('[name="contact[zip]"]')),
      'Items':        path === 'quick' ? '' : val(descField),
      'Availability': path === 'quick' ? val(messageQuick) : val(messageFull),
      'Photos':       uploadedPhotos().length
                        ? uploadedPhotos().map(function (p) { return p.url; }).join('\n')
                        : ''
    };
  }

  /* Rows are built as DOM nodes, never as an HTML string.
     v2.0 rebuilt reviewBox.innerHTML on every pass of the loop, which
     re-parsed the markup and wiped the textContent already written
     into the earlier rows - so only the last row kept its value and
     the visitor saw a table of empty labels. Values are also written
     with textContent rather than interpolated, so a visitor typing
     < or & into a textarea cannot inject markup here. */
  function reviewRow(key, value) {
    var row = document.createElement('div');
    row.className = 'gsb-review__row';

    var k = document.createElement('span');
    k.className = 'gsb-review__key';
    k.textContent = key;

    var v = document.createElement('span');
    v.className = 'gsb-review__val';
    v.textContent = value;

    row.appendChild(k);
    row.appendChild(v);
    return row;
  }

  function renderReview() {
    if (!reviewBox) return;

    var data = collect();
    var frag = document.createDocumentFragment();
    var rows = 0;

    Object.keys(data).forEach(function (key) {
      var value = data[key];
      if (!value) return;
      var label = key === 'Photos' ? uploadedPhotos().length + ' attached' : value;
      frag.appendChild(reviewRow(key, label));
      rows++;
    });

    reviewBox.innerHTML = '';

    if (!rows) {
      reviewBox.appendChild(reviewRow('', 'Nothing to review yet.'));
      return;
    }

    reviewBox.appendChild(frag);
  }

  function composeBody() {
    var data = collect();
    var lines = [];
    Object.keys(data).forEach(function (key) {
      if (data[key]) lines.push(key + ': ' + data[key]);
    });
    return lines.join('\n');
  }

  /* ---------- Navigation ---------- */

  form.addEventListener('click', function (e) {
    var choose = e.target.closest('[data-gsb-choose]');
    if (choose) {
      choosePath(choose.getAttribute('data-gsb-choose'));
      return;
    }

    if (e.target.closest('[data-gsb-change]')) {
      resetToChooser();
      return;
    }

    var back = e.target.closest('[data-gsb-back]');
    if (back) {
      var order = stepsForPath();
      var idx = order.indexOf(current);
      if (idx <= 1) resetToChooser();
      else showStep(order[idx - 1]);
      return;
    }

    var next = e.target.closest('[data-gsb-next]');
    if (next) {
      if (!validateStep(current)) return;
      if (current === 2) renderReview();
      var seq = stepsForPath();
      var at = seq.indexOf(current);
      if (at > -1 && at < seq.length - 1) showStep(seq[at + 1]);
      return;
    }
  });

  if (changeBtn) {
    changeBtn.addEventListener('click', function (e) { e.preventDefault(); });
  }

  /* ---------- Submit ---------- */

  form.addEventListener('submit', function (e) {
    if (submitting) { e.preventDefault(); return; }

    // Honeypot: a bot filled the field no human can see.
    if (honeypot && honeypot.value) {
      e.preventDefault();
      return;
    }

    if (!validateStep(1)) {
      e.preventDefault();
      if (current !== 1) showStep(1);
      return;
    }

    if (stillUploading()) {
      e.preventDefault();
      toast('Your photos are still uploading. One moment.', true);
      return;
    }

    try {
      if (bodyInput) bodyInput.value = composeBody();
    } catch (err) {
      e.preventDefault();
      toast('Something went wrong preparing your submission. Please call us at (949) 779-1940.', true);
      return;
    }

    submitting = true;
    var btn = e.submitter || form.querySelector('[data-gsb-submit], [data-gsb-quick-submit]');
    if (btn) {
      btn.classList.add('is-busy');
      btn.textContent = 'Sending…';
    }
  });

  /* ---------- Post-submit states ---------- */

  if (window.gsbFormSuccess) {
    for (var s = 0; s < steps.length; s++) steps[s].classList.remove('is-active');
    if (progress) progress.hidden = true;
    var ok = form.querySelector('[data-gsb-success]');
    if (ok) { ok.focus(); scrollToForm(); }
  } else if (window.gsbFormError) {
    showStep(0);
    scrollToForm();
  } else {
    showStep(0);
  }
})();